import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useCompleteOnboarding } from "@/hooks/use-accounts";
import { AddDemoAccount } from "@/components/v2/add-demo-account";
import { useAddDemoAccountStore } from "@/stores/add-demo-account-store";

type WelcomePath = "explore" | "create" | "skip";

const WELCOME_OPTIONS: {
  value: WelcomePath;
  label: string;
  description: string;
}[] = [
  {
    value: "explore",
    label: "Explore with a demo account",
    description: "See Adva in action with pre-loaded data. No setup required.",
  },
  {
    value: "create",
    label: "Create my own business",
    description: "Set up your business profile and start fresh.",
  },
  {
    value: "skip",
    label: "Skip for now",
    description: "Go straight to the dashboard. You can set up later.",
  },
];

export default function WelcomePage() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<WelcomePath>("explore");
  const { mutateAsync: complete, isPending } = useCompleteOnboarding();
  const openDemoDialog = useAddDemoAccountStore((s) => s.openDialog);

  async function handleContinue() {
    // Stamp onboarding complete for ALL three paths: choosing a fork IS the
    // first-run decision, so /welcome should not be re-offered automatically
    // even if the user later abandons demo-import or business setup (the
    // Settings "Explore Adva" link remains the manual re-entry). Intentional.
    try {
      await complete();
    } catch (error) {
      // The mutation only rejects on a genuine write failure (a refreshSession
      // hiccup is non-fatal inside the hook). Surface it and stay put rather
      // than navigating away from an un-stamped account.
      console.error("[WelcomePage] completeOnboarding failed:", error);
      toast.error(
        "Something went wrong setting up your account. Please try again.",
      );
      return;
    }
    if (selected === "explore") {
      // Open the demo-provisioning dialog in place. /welcome renders full-screen
      // outside <AppLayoutV2>, so the layout's <AddDemoAccount> isn't in this
      // tree — the page mounts its own instance below and drives it through the
      // shared Zustand store.
      openDemoDialog();
    } else if (selected === "create") {
      navigate("/settings/general");
    } else {
      navigate("/dashboard");
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[color:var(--color-bg)] p-6">
      <div className="w-full max-w-lg space-y-8">
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold text-[color:var(--color-text-hi)]">
            Welcome to Adva
          </h1>
          <p className="text-sm text-[color:var(--color-text-lo)]">
            How would you like to get started?
          </p>
        </div>

        <RadioGroup
          value={selected}
          onValueChange={(v) => setSelected(v as WelcomePath)}
          className="space-y-3"
        >
          {WELCOME_OPTIONS.map((option) => (
            <Label
              key={option.value}
              htmlFor={`welcome-${option.value}`}
              className={cn(
                "group flex cursor-pointer items-start gap-4 rounded-lg border-2 p-4 transition-colors",
                selected === option.value
                  ? "border-[color:var(--color-accent)] bg-[color:var(--color-accent)]/5"
                  : "border-[color:var(--color-border)] hover:border-[color:var(--color-border)]/80",
              )}
            >
              <RadioGroupItem
                value={option.value}
                id={`welcome-${option.value}`}
                className="mt-0.5"
              />
              <div className="flex-1 space-y-0.5">
                <span className="block text-sm font-medium text-[color:var(--color-text-hi)]">
                  {option.label}
                </span>
                <span className="block text-xs text-[color:var(--color-text-lo)]">
                  {option.description}
                </span>
              </div>
            </Label>
          ))}
        </RadioGroup>

        <Button
          onClick={handleContinue}
          disabled={isPending}
          className="w-full"
        >
          {isPending ? "Setting up…" : "Continue"}
        </Button>
      </div>

      {/* /welcome is full-screen (outside AppLayoutV2), so it mounts its own
          dialog instance. Driven open by openDemoDialog() in the explore fork
          via the shared add-demo-account store. */}
      <AddDemoAccount />
    </div>
  );
}
